
import {DataTable} from "../ui/dataTable";
import {PermissionedDirectoryEntry} from "@models/permissions";
import {
    useAuthInfo,
} from "@propelauth/react";
import {ColumnDef} from "@tanstack/react-table";
import React, {Dispatch, SetStateAction, useEffect, useMemo, useState} from "react";
import {Link, useParams} from "react-router-dom";

import {Button} from "../ui/button";
import {Input} from "../ui/input";
// import InviteBidderByEmail from "./InviteByEmail";
import {SortableHeader} from "../ui/tableUtils";
import InviteTeamMember from "./InviteTeamMember";
import {ProjectContext} from "../../contexts/ProjectContext";
import {CompanyType} from "../../models/company";
import {useToast} from "../ui/use-toast";
import {fetchWithToken} from "../../api/apiBase";
import {Badge} from "../ui/badge";
import {XCircleIcon} from "lucide-react";
import JoinProjectRequestList from "../projects/JoinProjectRequestList";
import InviteToQuickLink from "../qlink/QuickLinkInvites";
import {BulkInviteDialog} from "../bulkInvite/BulkInviteDialog";

export const base_directory_columns: ColumnDef<PermissionedDirectoryEntry>[] =
    [
        {
            header: ({column}) => (<SortableHeader column={column} name={"Name"}/>),
            accessorKey: "user.name",
            accessorFn: (row) => row.user?.name || 'n/a',
        },
        {
            header: ({column}) => (<SortableHeader column={column} name={"Company"}/>),
            accessorKey: "company.name",
            accessorFn: (row) => row.company?.name || 'n/a',
        },
        {
            header: ({column}) => (<SortableHeader column={column} name={"Title"}/>),
            accessorKey: "user.title",
        },
        {
            header: ({column}) => (<SortableHeader column={column} name={"Email"}/>),
            accessorKey: "user.email",
            cell: ({row}) => (
                <Link
                    to={`/profile/${row.original.user.email}`}>
                    {row.original.user.email}
                </Link>)
        },

    ];

export const company_directory_columns: ColumnDef<PermissionedDirectoryEntry>[] =
    [
        ...base_directory_columns,
        {
            header: ({column}) => (<SortableHeader column={column} name={"Status"}/>),
            id: "status",
            accessorFn: (row) => row.user?.has_login ,
            cell: ({row}) =>
                <Badge variant={row.original.user?.has_login ? "ok" : "destructive"}>
                    {row.original.user?.has_login ? "Active" : "Pending"}
                </Badge>
        },
    ];

export const project_directory_columns: ColumnDef<PermissionedDirectoryEntry>[] =
    [
        // {
        //     header: "Type",
        //     accessorKey: "permission_target_type",
        // },
        ...company_directory_columns,
    ]


type DirectoryNavProps = {
    entries: PermissionedDirectoryEntry[]
    setEntries?: Dispatch<SetStateAction<PermissionedDirectoryEntry[]>>;
    children?: React.ReactNode;
}

export function DirectoryMenu({entries, children}: DirectoryNavProps) {
    const projectUserContext = React.useContext(ProjectContext);
    const onInvite = (email: string) => {

    };

    const onTeamMemberAdded = (email: string) => {

    };

    return <div className={"container mx-auto flex items-center h-16 px-4"}>
        <div className="flex-1">{children}</div>
        <div className="flex items-center ml-auto">

            <InviteTeamMember
                onInvite={onTeamMemberAdded}
                currentUsers={entries.map(x => x.user.email)}/>

            {projectUserContext?.role === CompanyType.developer &&
                <>
                    <InviteToQuickLink onInvite={onInvite}/>
                    <BulkInviteDialog />
                    <JoinProjectRequestList />
                </>}

        </div>
    </div>
}

export function ScopeDirectoryView() {
    const {accessToken} = useAuthInfo();
    const {project_id, scope_id} = useParams<string>();
    const {toast} = useToast();
    const [directoryEntries, setDirectoryEntries] = useState<PermissionedDirectoryEntry[]>([]);
    // const projectUserContext = React.useContext(ProjectContext);

    const reload = (pid: string | undefined, sid: string | null) => {
        fetchWithToken(`/projects/${pid}/scope/${sid}/directory`, accessToken as string)
            .then((data) => {
                setDirectoryEntries(data);
                console.log('entries', data)
            })
            .catch((error) => {
                console.log("error", error);
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: "Error loading Directory" + String(error),
                });
            });
    }

    useEffect(() => {
        if (project_id && scope_id){
            reload(project_id, scope_id)
        }
    }, [project_id, scope_id]);

    return (
        <div>
            {directoryEntries.length > 0 &&
                <DataTable
                    data={directoryEntries}
                    columns={base_directory_columns}/>
            }
        </div>
    );
}

/**
 * Simple component that displays a table of directory entries
 * we are going to make an API call to get the directory entries....
 * */
export default function ProjectDirectoryView() {
    const {accessToken} = useAuthInfo();
    const {project_id} = useParams<string>();
    const {toast} = useToast();
    const [directoryEntries, setDirectoryEntries] = useState<PermissionedDirectoryEntry[]>([]);

    useEffect(() => {
        fetchWithToken(`/projects/${project_id}/directory`, accessToken as string)
            .then((data) => {
                setDirectoryEntries(data);
            })
            .catch((error) => {
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: "Error loading Directory" + String(error),
                });
            });
    }, [project_id]);

    const [search, setSearch] = useState("");
    const filteredEntries = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return directoryEntries;
        return directoryEntries.filter((x) =>
            [x.user?.name, x.user?.email, x.user?.title, x.company?.name]
                .some((v) => (v ?? "").toLowerCase().includes(q)));
    }, [directoryEntries, search]);

    const other_cols: ColumnDef<PermissionedDirectoryEntry>[] = [
        {
            header: "Actions",
            id: "ack",
            cell: ({row}) => <Button size="sm" variant="outline"><XCircleIcon/></Button>
        }
    ]

    return (
        <div>
            <DirectoryMenu entries={directoryEntries}>
                <Input
                    className="max-w-sm"
                    placeholder="Search name, company, title, email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}/>
            </DirectoryMenu>
            <br/>
            {directoryEntries.length > 0 &&
                <DataTable
                    data={filteredEntries}
                    columns={[...project_directory_columns
                        // ,...other_cols
                ]}/>
            }
        </div>
    );
}

